import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getTierLimits } from '@/lib/tierLimits';
import { USER_SUMMARY_UNLOCKS_COLUMNS } from './columnConstants';
import { standardQueryDefaults } from './queryHelpers';

/**
 * Summary unlock record from database
 */
export interface SummaryUnlock {
  id: string;
  user_id: string;
  bill_id: number;
  unlocked_at: string;
  expires_at: string;
}

/** Daily unlock limit for free tier users - sourced from tier config */
const DAILY_UNLOCK_LIMIT = getTierLimits('free').summaries.dailyUnlocks ?? 5;

const QUERY_KEY = ['summary-unlocks'] as const;

/**
 * Fetch all active (non-expired) unlocks for the current user
 */
async function fetchActiveUnlocks(userId: string): Promise<SummaryUnlock[]> {
  const { data, error } = await supabase
    .from('user_summary_unlocks')
    .select(USER_SUMMARY_UNLOCKS_COLUMNS)
    .eq('user_id', userId)
    .gt('expires_at', new Date().toISOString())
    .order('unlocked_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch summary unlocks: ${error.message}`);
  }

  return data || [];
}

/**
 * Create an unlock for a bill (valid for 24 hours)
 */
async function unlockSummary(userId: string, billId: number): Promise<SummaryUnlock> {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now

  const { data, error } = await supabase
    .from('user_summary_unlocks')
    .insert({
      user_id: userId,
      bill_id: billId,
      unlocked_at: now.toISOString(),
      expires_at: expiresAt.toISOString(),
    })
    .select()
    .single();

  if (error) {
    // Handle unique constraint violation (already unlocked)
    if (error.code === '23505') {
      throw new Error('This summary is already unlocked');
    }
    throw new Error(`Failed to unlock summary: ${error.message}`);
  }

  return data;
}

/**
 * Hook to manage summary unlocks for free tier users
 *
 * Paid users (citizen, professional) have unlimited access and don't need unlocks.
 * Free users get 5 temporary unlocks per day, each lasting 24 hours.
 *
 * @returns Object with unlock data and mutation functions
 *
 * @example
 * const {
 *   isUnlocked,
 *   canUnlock,
 *   remainingUnlocks,
 *   unlockSummary,
 *   isUnlocking
 * } = useSummaryUnlocks();
 *
 * // Check if a bill's summary is unlocked
 * if (isUnlocked(123)) { ... }
 *
 * // Unlock a summary
 * await unlockSummary.mutateAsync(123);
 */
export function useSummaryUnlocks() {
  const { user, isPaidUser } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  // Query to fetch active unlocks
  const unlocksQuery = useQuery({
    queryKey: [...QUERY_KEY, userId],
    queryFn: () => fetchActiveUnlocks(userId!),
    ...standardQueryDefaults,
    enabled: !!userId && !isPaidUser, // Only fetch for free users
  });

  // Derive a Set of unlocked bill IDs for O(1) lookup
  const unlockedBillIds = useMemo(
    () => new Set(unlocksQuery.data?.map((u) => u.bill_id) ?? []),
    [unlocksQuery.data]
  );

  // Count unlocks used today
  const todayUnlockCount = useMemo(() => {
    if (!unlocksQuery.data) return 0;
    const today = new Date().toISOString().split('T')[0];
    return unlocksQuery.data.filter((u) => u.unlocked_at.startsWith(today)).length;
  }, [unlocksQuery.data]);

  // Calculate remaining unlocks
  const remainingUnlocks = useMemo(() => {
    if (isPaidUser) return null; // Unlimited for paid users
    return Math.max(0, DAILY_UNLOCK_LIMIT - todayUnlockCount);
  }, [isPaidUser, todayUnlockCount]);

  // Check if user can unlock more summaries
  const canUnlock = useMemo(() => {
    if (isPaidUser) return true; // Paid users always can (though they don't need to)
    return todayUnlockCount < DAILY_UNLOCK_LIMIT;
  }, [isPaidUser, todayUnlockCount]);

  // Helper to check if a bill's summary is unlocked
  const isUnlocked = (billId: number): boolean => {
    if (isPaidUser) return true; // Paid users have all summaries unlocked
    return unlockedBillIds.has(billId);
  };

  // Unlock mutation with optimistic update
  const unlockMutation = useMutation({
    mutationFn: (billId: number) => unlockSummary(userId!, billId),
    onMutate: async (billId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [...QUERY_KEY, userId] });

      // Snapshot previous value
      const previousUnlocks = queryClient.getQueryData<SummaryUnlock[]>([...QUERY_KEY, userId]);

      // Optimistically add the new unlock
      const now = new Date();
      const optimisticUnlock: SummaryUnlock = {
        id: `temp-${billId}`,
        user_id: userId!,
        bill_id: billId,
        unlocked_at: now.toISOString(),
        expires_at: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
      };

      queryClient.setQueryData<SummaryUnlock[]>([...QUERY_KEY, userId], (old) => [
        optimisticUnlock,
        ...(old ?? []),
      ]);

      return { previousUnlocks };
    },
    onError: (_err, _billId, context) => {
      // Rollback on error
      if (context?.previousUnlocks) {
        queryClient.setQueryData([...QUERY_KEY, userId], context.previousUnlocks);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, userId] });
    },
  });

  return {
    // Data
    unlocks: unlocksQuery.data ?? [],
    unlockedBillIds,

    // Limits
    dailyLimit: DAILY_UNLOCK_LIMIT,
    todayUnlockCount,
    remainingUnlocks,
    canUnlock,

    // State
    isLoading: unlocksQuery.isLoading,
    isError: unlocksQuery.isError,
    error: unlocksQuery.error,

    // Helpers
    isUnlocked,

    // Mutations
    unlockSummary: unlockMutation,
    isUnlocking: unlockMutation.isPending,

    // Refetch
    refetch: unlocksQuery.refetch,
  };
}

/**
 * Lightweight hook to just check if a specific bill is unlocked
 */
export function useIsBillUnlocked(billId: number) {
  const { user, isPaidUser } = useAuth();
  const userId = user?.id;

  const { data: isUnlocked, isLoading } = useQuery({
    queryKey: [...QUERY_KEY, userId, billId],
    queryFn: async () => {
      if (isPaidUser) return true;

      const { data, error } = await supabase
        .from('user_summary_unlocks')
        .select('id')
        .eq('user_id', userId!)
        .eq('bill_id', billId)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (error) {
        return false;
      }

      return !!data;
    },
    ...standardQueryDefaults,
    enabled: !!userId,
  });

  return {
    isUnlocked: isPaidUser || (isUnlocked ?? false),
    isLoading,
  };
}
